import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Integration } from '../../../../shared/interfaces/integration';
import { Table } from '../../../../table/Table';
import { shouldResumePipelineCreate } from '../../../utils/pipelineUtil';
import { getDataIdGenerator } from '../../../../shared/utils/dataId';
import { pipelineListColumns } from './PipelineListColumns';
import { ListComponentBaseProps } from '../../../../entity-list/interface';
import { useAnalyticsTracker } from '../../../../tracking/hooks/useAnalyticsTracker';
import { PipelineListTrackers } from '../../../tracking/pipeline-list-trackers';

export function PipelineListTable({
  listMode: mode,
  data,
  tableProps,
  dataId,
  hideColumns = [],
  sortThroughHeader = {}
}: ListComponentBaseProps<Integration[]>) {
  const updatedPipelines = useRef(new Map<number, Integration>());
  const [pipelines, setPipelines] = useState<Integration[]>(data);

  useEffect(() => {
    const _pipelines = data.map(i => ({
      ...i,
      ...(updatedPipelines.current.get(i.id) || {})
    }));

    setPipelines(_pipelines);
  }, [data]);

  const navigate = useNavigate();
  const { eventTrack } = useAnalyticsTracker();

  const dataIdGenerator = getDataIdGenerator('pipeline-list-table', dataId);

  const { rows, columns } = useMemo(() => {
    let _columns = pipelineListColumns();

    if (hideColumns.length) {
      _columns = _columns.filter(column => !(hideColumns.indexOf(column.field) > -1));
    }

    if (sortThroughHeader) {
      _columns = _columns.map(column => {
        const sortKey = sortThroughHeader[column.field];
        if (sortKey) {
          return { ...column, canSort: true, sortKey };
        }
        return column;
      });
    }

    const _rows = pipelines.map(pipeline => ({
      data: pipeline,
      tabIndex: 0,
      id: pipeline.id,
      role: 'button',
      dataId: dataIdGenerator(`row-${pipeline.id}`),
      onClick: (p: Integration) => {
        eventTrack({
          action: PipelineListTrackers.PIPELINE_TRACKER_PIPELINE_LIST_PIPELINE_CLICK,
          properties: {
            pipelineId: pipeline.id,
            status: p.status.value
          }
        });

        if (shouldResumePipelineCreate(p.status.value)) {
          navigate(`/pipeline/edge/${p.id}/create`);
        } else {
          navigate(`/pipeline/edge/${p.id}/`);
        }
      }
    }));

    return {
      rows: _rows,
      columns: _columns
    };
  }, [pipelines, mode]);

  return <Table columns={columns} rows={rows} dataId={dataIdGenerator('')} {...tableProps} />;
}
